/**
 * The bell in the top bar. The last few notifications drop straight down from
 * it — enough to see what happened and act on it without leaving the page —
 * with the full history one click away.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsPhone } from '@/hooks/useIsPhone';
import type { Lang } from '@/data/types';
import { useNotifications, type PortalNotif } from '@/hooks/useNotifications';
import { pushSupported, pushPermission, enablePush } from '@/lib/webPush';
import { markSeen } from '@/hooks/useSeen';
import { KBIcon, type IconName } from '@/components/kbIcons';

const T = (lang: Lang, en: string, es: string, fr: string, pt: string) =>
  lang === 'es' ? es : lang === 'fr' ? fr : lang === 'pt' ? pt : en;

const ICON: Record<string, IconName> = {
  assigned: 'mywork', collab: 'mywork', reminder: 'bell', note: 'edit', 'note-tag': 'edit',
  'note-update': 'edit', handover: 'logbook', 'ho-reminder': 'logbook', 'ho-mention': 'logbook',
  'ho-alert': 'calendar', 'ho-team': 'users', 'waiting-on-you': 'bell', 'planner-digest': 'clock', 'ticket-new': 'services', 'ticket-back': 'eye',
  'ticket-stage': 'logbook', 'task-started': 'check', 'docs-pending': 'documents',
  'docs-reminder': 'bell', 'link-approved': 'check', 'link-rejected': 'x', 'link-request': 'pin',
  access: 'users', 'app-error': 'brain', feedback: 'feedback', 'feedback-reply': 'feedback',
  'resource-request': 'resources', 'resource-approved': 'check', 'resource-rejected': 'x',
};

/** WHAT HAPPENED, in two words — the dropdown used to say who and which
 *  service but not the action, so every row required the full page. */
const KIND: Record<string, [string, string, string, string]> = {
  assigned: ['Ticket assigned', 'Ticket asignado', 'Ticket assigné', 'Ticket atribuído'],
  collab: ['Added as collaborator', 'Añadido como colaborador', 'Ajouté comme collaborateur', 'Adicionado como colaborador'],
  reminder: ['Reminder', 'Recordatorio', 'Rappel', 'Lembrete'],
  note: ['New note', 'Nota nueva', 'Nouvelle note', 'Nova nota'],
  'note-tag': ['Tagged in a note', 'Te taguearon en una nota', 'Mentionné dans une note', 'Marcado numa nota'],
  'note-update': ['Note edited', 'Nota editada', 'Note modifiée', 'Nota editada'],
  handover: ['Handover', 'Handover', 'Handover', 'Handover'],
  'ho-reminder': ['Trip reminder', 'Recordatorio de viaje', 'Rappel de voyage', 'Lembrete de viagem'],
  'ho-mention': ['Mentioned in a handover', 'Te mencionaron en un handover', 'Mentionné dans un handover', 'Mencionado num handover'],
  'ho-alert': ['Handover alert', 'Alerta de handover', 'Alerte de handover', 'Alerta de handover'],
  /* The whole team hears that somebody is leaving — see the note where it is
     sent: an absence nobody knew about is the one that hurts. */
  'ho-team': ['Someone is going away', 'Alguien se va de vacaciones', 'Quelqu’un part en congé', 'Alguém vai de férias'],
  'planner-digest': ['Planner weekly recap', 'Resumen semanal del planificador', 'Récap hebdo du planificateur', 'Resumo semanal do planificador'],
  'waiting-on-you': ['Someone waits on you', 'Esperan una acción tuya', 'On attend une action de vous', 'Esperam uma ação sua'],
  'ticket-new': ['New ticket assigned', 'Ticket nuevo asignado', 'Nouveau ticket assigné', 'Novo ticket atribuído'],
  'ticket-back': ['Back on the board', 'Vuelve al tablero', 'Revient au tableau', 'Volta ao quadro'],
  'ticket-stage': ['Stage changed', 'Cambio de etapa', "Changement d'étape", 'Mudança de etapa'],
  'task-started': ['Task started', 'Tarea empezada', 'Tâche démarrée', 'Tarefa iniciada'],
  'docs-pending': ['Docs pending review', 'Docs por revisar', 'Docs à réviser', 'Docs por rever'],
  'docs-reminder': ['Docs reminder', 'Recordatorio de docs', 'Rappel de docs', 'Lembrete de docs'],
  'link-approved': ['Link approved', 'Link aprobado', 'Lien approuvé', 'Link aprovado'],
  'link-rejected': ['Link rejected', 'Link rechazado', 'Lien rejeté', 'Link rejeitado'],
  'link-request': ['Link proposed', 'Link propuesto', 'Lien proposé', 'Link proposto'],
  access: ['Access request', 'Petición de acceso', "Demande d'accès", 'Pedido de acesso'],
  'app-error': ['Portal error', 'Error del portal', 'Erreur du portail', 'Erro do portal'],
  feedback: ['New feedback', 'Feedback nuevo', 'Nouveau feedback', 'Novo feedback'],
  'feedback-reply': ['Your feedback was answered', 'Respondieron tu feedback', 'Réponse à votre feedback', 'Responderam ao teu feedback'],
  'resource-request': ['Resource proposed', 'Recurso propuesto', 'Ressource proposée', 'Recurso proposto'],
  'resource-approved': ['Resource published', 'Recurso publicado', 'Ressource publiée', 'Recurso publicado'],
  'resource-rejected': ['Resource rejected', 'Recurso rechazado', 'Ressource refusée', 'Recurso recusado'],
};
const kindLabel = (lang: Lang, kind: string): string => {
  const k = KIND[kind];
  return k ? T(lang, k[0], k[1], k[2], k[3]) : '';
};

/** "9:48", "yesterday", "2 days ago" — the shape a dropdown needs. */
function when(lang: Lang, iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that = new Date(d); that.setHours(0, 0, 0, 0);
  const days = Math.round((today.getTime() - that.getTime()) / 86_400_000);
  if (days === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (days === 1) return T(lang, 'Yesterday', 'Ayer', 'Hier', 'Ontem');
  if (days < 7) return T(lang, `${days} days ago`, `Hace ${days} días`, `Il y a ${days} jours`, `Há ${days} dias`);
  return d.toLocaleDateString(lang, { day: 'numeric', month: 'short' });
}

export function NotifBell({ lang, isSuper, onOpenAll, onGo }: {
  lang: Lang;
  isSuper: boolean;
  onOpenAll: () => void;
  /** Act on one notification (same routing the full page uses). */
  onGo: (n: PortalNotif) => void;
}) {
  // The bell is the actionable queue: it lists exactly what it counts. Portal
  // errors, account requests and note edits are still recorded — they live
  // under "Others" on the full page, where they don't chase anyone.
  const { unseenImportant, unseen, count, seen } = useNotifications(isSuper);
  /* Lo informativo de hoy, sin leer: no suena ni cuenta, pero se dice en una
     línea al pie para que «estás al día» no sea mentira. */
  /* ── OFRECER EL PUSH UNA VEZ, DONDE SE MIRA ──
     El interruptor vivía en Ajustes → Notificaciones, y en toda la casa había
     DOS suscripciones. Se ofrece aquí, en la campana, una sola vez por
     navegador; «ahora no» lo cierra para siempre en ese navegador. */
  const [pushAsk, setPushAsk] = useState<boolean>(() => {
    try { return pushSupported() && pushPermission() === 'default' && !localStorage.getItem('lexidy-push-asked'); } catch { return false; }
  });
  const [pushBusy, setPushBusy] = useState(false);
  const closePushAsk = () => { try { localStorage.setItem('lexidy-push-asked', '1'); } catch { /* modo privado */ } setPushAsk(false); };
  const today = new Date().toISOString().slice(0, 10);
  const infoToday = unseen.filter((n) => !n.important && (n.date || '').slice(0, 10) === today).length;
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (box.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  // ONLY what is still unread. The bell is the "needs me" queue, not a
  // history: the moment something is marked read — here, or from the full
  // page, or on another device — it leaves this dropdown. The archive lives
  // behind "View all notifications", where read rows belong.
  /* ── UN CASO, UNA FILA ──
     Tres cosas del mismo caso el mismo día eran tres filas iguales de las
     seis que caben. Ahora la primera lleva un «+2» y abre el caso; las otras
     se marcan vistas con ella. */
  const grouped = (() => {
    const out: (PortalNotif & { more: string[] })[] = [];
    const byRef = new Map<string, (PortalNotif & { more: string[] })>();
    for (const n of unseenImportant) {
      const key = n.refId ? `${n.target}|${n.refId}` : '';
      const head = key ? byRef.get(key) : undefined;
      if (head) { head.more.push(n.id); continue; }
      const row = { ...n, more: [] as string[] };
      if (key) byRef.set(key, row);
      out.push(row);
    }
    return out;
  })();
  const recent = grouped.slice(0, 6);
  /* The frosted topbar's backdrop-filter makes it the containing block for
     any fixed child — on phones the dropdown sat UNDER the sidebar, clipped.
     There it renders through a portal on <body>; the phone CSS (fixed,
     viewport-wide) then means what it says. */
  const phone = useIsPhone();
  const menuRef = useRef<HTMLDivElement>(null);
  const label = T(lang, 'Notifications', 'Notificaciones', 'Notifications', 'Notificações');

  return (
    <div className="tb-pop nb" ref={box}>
      <button
        className={`tb-icon-btn nb-btn ${count > 0 ? 'has' : ''}`}
        onClick={() => setOpen((v) => !v)}
        title={count > 0 ? `${label} (${count})` : label}
        aria-label={label}
      >
        <KBIcon name="bell" size={18} />
        {count > 0 && <span className="nb-dot" key={count}>{count > 9 ? '9+' : count}</span>}
      </button>

      {open && (() => {
        const menu = (
        <div className="tb-menu tb-menu-right nb-pop" ref={menuRef}>
          <header className="nb-head">
            <strong>{label}</strong>
            {count > 0 && <span className="nb-new">{count} {T(lang, 'new', 'nuevas', 'nouvelles', 'novas')}</span>}
            {count > 0 && (
              <button className="nb-mark" onClick={() => markSeen(unseenImportant.map((n) => n.id))}>
                {T(lang, 'Mark all read', 'Marcar todas', 'Tout marquer', 'Marcar todas')}
              </button>
            )}
          </header>

          {pushAsk && (
            <div className="nb-push">
              <span>{T(lang,
                'Get what asks for you on your phone — tasks, reminders, deadlines. Nothing else.',
                'Recibe en el móvil lo que te pide algo — tareas, recordatorios, plazos. Nada más.',
                'Recevez sur le téléphone ce qui vous demande quelque chose — tâches, rappels, délais. Rien d’autre.',
                'Receba no telemóvel o que lhe pede algo — tarefas, lembretes, prazos. Nada mais.')}</span>
              <button className="nb-mark" disabled={pushBusy}
                onClick={() => { setPushBusy(true); void enablePush().finally(() => { setPushBusy(false); closePushAsk(); }); }}>
                {pushBusy ? '…' : T(lang, 'Turn on', 'Activar', 'Activer', 'Ativar')}
              </button>
              <button className="nb-mark quiet" onClick={closePushAsk}>{T(lang, 'Not now', 'Ahora no', 'Pas maintenant', 'Agora não')}</button>
            </div>
          )}
          {recent.length === 0 ? (
            <p className="nb-empty">{T(lang, "You're all caught up.", 'Estás al día.', 'Vous êtes à jour.', 'Está em dia.')}</p>
          ) : (
            <div className="nb-list">
              {recent.map((n) => (
                <button
                  key={n.id}
                  className={`nb-row ${seen.has(n.id) ? '' : 'unread'}`}
                  onClick={() => { if (n.more.length) markSeen(n.more); setOpen(false); onGo(n); }}
                >
                  <span className="nb-ico"><KBIcon name={ICON[n.kind] || 'bell'} size={15} /></span>
                  <span className="nb-body">
                    <span className="nb-title">
                      {/* One line, always. A title that wraps turns a tidy
                          list into paragraphs of different heights, and the
                          eye stops being able to run down it. */}
                      <span className="nb-title-t">{n.text}</span>
                      {n.more.length > 0 && <span className="nb-grp" title={T(lang, 'More on this case', 'Más en este caso', 'Autres sur ce dossier', 'Mais neste caso')}>+{n.more.length}</span>}
                      {!seen.has(n.id) && <span className="nb-unread-dot" />}
                    </span>
                    {/* Client and WHAT HAPPENED, nothing else. The service used
                        to trail the action here and there is no room for it in
                        a popover this narrow: it truncated mid-word on nearly
                        every row, so it added a ragged edge instead of a fact.
                        It is still on every row of the full notifications page,
                        which has the width for it. */}
                    <span className="nb-meta">
                      {kindLabel(lang, n.kind) && <em className="nb-kind">{kindLabel(lang, n.kind)}</em>}
                      {/* TWO ROWS, SAME NAME: a client with two new tickets
                          read as the same notification twice (Cindra Feuer,
                          16 Sept). When the client name repeats in the list,
                          the service says which is which — short, on the
                          meta line, so the design does not move. */}
                      {n.by && recent.filter((o) => o.text === n.text).length > 1 && (
                        <span className="nb-which" title={n.by}>· {n.by}</span>
                      )}
                    </span>
                    <span className="nb-when">{when(lang, n.date)}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Le réglage des mails d'Actes n'est PAS ici. Cette liste est la
              file de ce qui attend, pas un panneau de réglages : il vit avec
              les autres réglages de courrier, dans « Voir toutes les
              notifications » → « Comment vous êtes averti ». */}
          {infoToday > 0 && (
            <p className="nb-also">
              {T(lang,
                `${infoToday} informational today — stage moves, notes, status changes.`,
                `${infoToday} informativas hoy — etapas, notas, cambios de estado.`,
                `${infoToday} informatives aujourd’hui — étapes, notes, statuts.`,
                `${infoToday} informativas hoje — etapas, notas, estados.`)}
            </p>
          )}
          <button className="nb-all" onClick={() => { setOpen(false); onOpenAll(); }}>
            {T(lang, 'View all notifications', 'Ver todas las notificaciones', 'Voir toutes les notifications', 'Ver todas as notificações')}
          </button>
        </div>
        );
        return phone ? createPortal(menu, document.body) : menu;
      })()}
    </div>
  );
}
